import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Users, FileText, Link2, Wallet, Search, ChevronRight } from "lucide-react";
import { ClientDetail } from "@/components/chef/ClientDetail";

const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fdate = (s) => (s ? new Date(String(s).slice(0, 10) + "T12:00:00").toLocaleDateString("fr-FR") : "—");

export default function ChefClients() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try { const { data: r } = await api.get(`/chef/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`); setData(r); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-full" data-testid="chef-clients-page">
      <PageHeader eyebrow="Portail client" title="Vos clients & leur espace"
        sub="Chaque client ayant réservé dispose d'un espace personnel (lien sans mot de passe) : réservations et QR codes, documents que vous partagez, suivi des paiements, et bientôt devis & factures."
        right={
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#868E96]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher nom, email, téléphone…" data-testid="clients-search" className="h-11 pl-9 w-[300px]" />
          </div>
        } />

      <div className="p-6 lg:p-10 space-y-6">
        {data && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Clients" value={data.stats.clients} icon={Users} dark testid="clients-kpi-total" />
            <Kpi label="Documents partagés" value={data.stats.documents} icon={FileText} testid="clients-kpi-docs" />
            <Kpi label="Accès actifs" value={data.stats.active_links} icon={Link2} testid="clients-kpi-links" />
            <Kpi label="Reste à encaisser" value={eur(data.stats.total_due)} icon={Wallet} accent={data.stats.total_due > 0 ? "#E60000" : undefined} testid="clients-kpi-due" />
          </div>
        )}

        <div className="bg-white border border-[#DEE2E6]" data-testid="clients-list">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-4">Client</div><div className="col-span-2">Séjours</div><div className="col-span-2">Prochain séjour</div><div className="col-span-2">Paiements</div><div className="col-span-2 text-right">Espace client</div>
          </div>
          {!data || data.clients.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]" data-testid="clients-empty">Aucun client{q ? " ne correspond à cette recherche" : " — les clients apparaissent dès leur première réservation avec un email"}.</div>
          ) : data.clients.map((c) => (
            <button key={c.email} onClick={() => setSelected(c.email)} data-testid={`client-row-${c.email}`}
              className="w-full text-left grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-[#F1F3F5] last:border-b-0 text-sm hover:bg-[#F8F9FA] transition-colors">
              <div className="col-span-4"><div className="font-semibold">{c.nom || "—"}</div><div className="text-[11px] text-[#868E96] truncate">{c.email}{c.telephone ? ` · ${c.telephone}` : ""}</div></div>
              <div className="col-span-2"><b>{c.bookings_count}</b> <span className="text-[#868E96] text-xs">dont {c.upcoming_count} à venir</span></div>
              <div className="col-span-2 text-xs">{c.next_stay ? fdate(c.next_stay) : <span className="text-[#868E96]">—</span>}</div>
              <div className="col-span-2 text-xs"><div className="font-mono">{eur(c.total_spent)} réglés</div>{c.total_due > 0 && <div className="font-mono text-[#E60000]" data-testid={`client-due-${c.email}`}>{eur(c.total_due)} dus</div>}</div>
              <div className="col-span-2 flex items-center justify-end gap-2 text-xs">
                {c.documents_count > 0 && <span className="border border-[#DEE2E6] px-1.5 py-0.5 text-[10px]">{c.documents_count} doc</span>}
                <span className={`status-pill ${c.portal_link?.active ? "status-active" : "status-inactive"}`} data-testid={`client-link-${c.email}`}>{c.portal_link?.active ? "Accès actif" : "Pas d'accès"}</span>
                <ChevronRight className="h-4 w-4 text-[#868E96]" />
              </div>
            </button>
          ))}
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); load(); } }}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="client-sheet">
          <SheetHeader><SheetTitle className="font-display text-xl font-black">Fiche client</SheetTitle></SheetHeader>
          {selected && data && <ClientDetail email={selected} categories={data.categories} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, dark, accent, testid }) {
  return (
    <div className={`p-5 border ${dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6]"}`} data-testid={testid}>
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold ${dark ? "text-[#FFD700]" : "text-[#868E96]"}`}><Icon className="h-3 w-3" /> {label}</div>
      <div className="font-display text-2xl font-black mt-2" style={accent && !dark ? { color: accent } : {}}>{value}</div>
    </div>
  );
}
